import { useDuplicateArtwork } from "@/api/artwork/artwork";
import { tWithKey } from "@/lib/i18n-fallback";
import { incrementRecordNumber } from "@/lib/record-number";
import { zodResolver } from "@hookform/resolvers/zod";
import { isAxiosError } from "axios";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import DuplicateEntityDialog from "../modal/DuplicateEntityDialog";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";

const schema = z.object({
  oeuvresDescriptionNumeroEnregistrement: z
    .string()
    .trim()
    .min(1, { message: "required" }),
});

type FormType = z.infer<typeof schema>;

type DuplicateArtworkDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceArtworkId: string | undefined;
  sourceNumeroEnregistrement: string | undefined;
  onDuplicateSuccess: (created: {
    id: string;
    oeuvresDescriptionNumeroEnregistrement?: string;
  }) => void;
};

const DuplicateArtworkDialog = ({
  open,
  onOpenChange,
  sourceArtworkId,
  sourceNumeroEnregistrement,
  onDuplicateSuccess,
}: DuplicateArtworkDialogProps) => {
  const { t } = useTranslation();
  const form = useForm<FormType>({
    reValidateMode: "onSubmit",
    resolver: zodResolver(schema),
    defaultValues: {
      oeuvresDescriptionNumeroEnregistrement: "",
    },
  });
  const { mutateAsync, isPending } = useDuplicateArtwork();

  // Pre-fill the record number with an incremented suggestion each time the
  // dialog opens for a given source artwork.
  useEffect(() => {
    if (open) {
      form.reset({
        oeuvresDescriptionNumeroEnregistrement: incrementRecordNumber(
          sourceNumeroEnregistrement,
        ),
      });
    }
  }, [open, sourceNumeroEnregistrement, form]);

  const handleSubmit = async (data: FormType) => {
    if (!sourceArtworkId) return;
    try {
      const created = await mutateAsync({
        sourceId: sourceArtworkId,
        oeuvresDescriptionNumeroEnregistrement:
          data.oeuvresDescriptionNumeroEnregistrement,
      });
      onDuplicateSuccess({
        id: created.id,
        oeuvresDescriptionNumeroEnregistrement:
          created.oeuvresDescriptionNumeroEnregistrement,
      });
      onOpenChange(false);
      form.reset();
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 409) {
        form.setError("oeuvresDescriptionNumeroEnregistrement", {
          message: t("itemWithSameIdAlreadyExists"),
        });
        return;
      }
      form.setError("root", { message: t("unknownError") });
      throw err;
    }
  };

  return (
    <DuplicateEntityDialog<FormType>
      open={open}
      onOpenChange={onOpenChange}
      title={tWithKey(t, "duplicateArtwork", "Dupliquer la fiche d'œuvre")}
      form={form}
      onSubmit={handleSubmit}
      isLoading={isPending}
    >
      <FormField
        control={form.control}
        name="oeuvresDescriptionNumeroEnregistrement"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("oeuvresDescriptionNumeroEnregistrement")}</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormMessage>{form.formState.errors.root?.message}</FormMessage>
    </DuplicateEntityDialog>
  );
};

export default DuplicateArtworkDialog;
